import { useEffect, useMemo, useState } from "react";
import { Paper } from "@mui/material";
import { useMsal } from "@azure/msal-react";
import { useUserContext } from "../../contexts";
import { apiAccessRequest } from "../../constants";
import { BuildUrl } from "../../helpers";
import { SearchInput } from "../Common";

interface AuditLogUserSearchProps {
	label?: string;
	value?: string;
	onUserSelect?: (user?: string) => void;
}

export const AuditLogUserSearch = ({
	label = "User",
	value,
	onUserSelect
}: AuditLogUserSearchProps) => {
	const { instance } = useMsal();
	const { account } = useUserContext();
	const [searchValue, setSearchValue] = useState("");
	const [filteredUsers, setFilteredUsers] = useState<string[]>([]);
	const [isLoading, setIsLoading] = useState(false);

	useEffect(() => {
		setSearchValue(value ?? "");
	}, [value]);

	const handleSearchValueChange = (nextValue: string) => {
		setSearchValue(nextValue);

		if (nextValue.trim() === "" && value) {
			setFilteredUsers([]);
			onUserSelect?.(undefined);
		}
	};

	useEffect(() => {
		const searchTerm = searchValue.trim();
		const selectedValue = value?.trim().toLowerCase() ?? "";

		if (searchTerm.length < 1 || searchTerm.toLowerCase() === selectedValue) {
			setFilteredUsers([]);
			setIsLoading(false);
			return;
		}

		const fetchUsers = async () => {
			setIsLoading(true);
			try {
				const tokenResponse = await instance.acquireTokenSilent({
					account,
					scopes: apiAccessRequest.scopes
				});

				const response = await fetch(BuildUrl("Auditlog/usersearch"), {
					method: "POST",
					headers: {
						Authorization: `Bearer ${tokenResponse.accessToken}`,
						"Content-Type": "application/json"
					},
					body: JSON.stringify(
						searchTerm
							.split(/\s+/)
							.map((part) => part.trim())
							.filter((part) => part.length > 0)
					)
				});

				if (!response.ok) {
					throw new Error("Network response was not ok");
				}

				const contentLength = response.headers.get("Content-Length");
				if (response.status === 204 || contentLength === "0") {
					setFilteredUsers([]);
					return;
				}

				const result = (await response.json()) as string[];
				setFilteredUsers(result);
			} catch (error) {
				console.log("AuditLogUserSearch fetchUsers", error);
				setFilteredUsers([]);
			} finally {
				setIsLoading(false);
			}
		};

		void fetchUsers();
	}, [account, instance, searchValue, value]);

	const handleUserClick = (user?: string) => {
		setSearchValue(user ?? "");
		setFilteredUsers([]);
		onUserSelect?.(user);
	};

	const shouldShowDropdown = useMemo(() => {
		const selectedValue = value?.trim().toLowerCase() ?? "";
		return (
			searchValue.trim().length > 0 &&
			searchValue.trim().toLowerCase() !== selectedValue
		);
	}, [searchValue, value]);

	return (
		<div style={{ position: "relative", width: "100%" }}>
			<SearchInput
				label={label}
				value={searchValue}
				handleSearchValueChange={handleSearchValueChange}
				handleClear={() => handleUserClick(undefined)}
			/>
			{shouldShowDropdown && (
				<Paper
					elevation={3}
					style={{
						position: "absolute",
						top: "100%",
						left: 0,
						right: 0,
						zIndex: 10,
						maxHeight: 400,
						overflowY: "auto",
						marginTop: 8
					}}
				>
					{isLoading ? (
						<div style={{ padding: "12px 16px" }}>Loading...</div>
					) : filteredUsers.length > 0 ? (
						filteredUsers.map((user) => (
							<button
								key={user}
								type="button"
								onClick={() => handleUserClick(user)}
								style={{
									display: "block",
									width: "100%",
									padding: "12px 16px",
									textAlign: "left",
									border: 0,
									background: "transparent",
									cursor: "pointer"
								}}
							>
								{user}
							</button>
						))
					) : (
						<div style={{ padding: "12px 16px" }}>No users found</div>
					)}
				</Paper>
			)}
		</div>
	);
};
